import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, IconButton, Fade, useTheme } from '@mui/material';
import { WarningAmber as WarningIcon, ChevronLeft, ChevronRight, Close } from '@mui/icons-material';

interface WarningDisplayProps {
  warnings: string[];
  onDismiss?: () => void;
}

export const WarningDisplay: React.FC<WarningDisplayProps> = ({ warnings, onDismiss }) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset index if warnings change significantly (optional, but good practice)
  useEffect(() => {
    if (currentIndex >= warnings.length) {
      setCurrentIndex(0);
    }
  }, [warnings, currentIndex]);

  if (!warnings || warnings.length === 0) return null;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % warnings.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + warnings.length) % warnings.length);
  };

  return (
    <Fade in={warnings.length > 0}>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          zIndex: 2000,
          width: 350,
          maxWidth: '90vw',
          bgcolor: theme.palette.warning.light,
          color: theme.palette.warning.contrastText,
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.warning.main}`,
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
              {warnings.length} Issues Found While Saving
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={onDismiss}
            aria-label="close warnings"
            sx={{ mt: -0.5, mr: -0.5 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box 
          sx={{ 
            minHeight: 60, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            my: 1
          }}
        >
          <Typography variant="body2" color="text.primary" align="center">
            {warnings[currentIndex]}
          </Typography>
        </Box>

        {warnings.length > 1 && (
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
            <IconButton 
              size="small" 
              onClick={handlePrev} 
              disabled={warnings.length <= 1}
              aria-label="previous warning"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            
            <Typography variant="caption" color="text.secondary">
              {currentIndex + 1} of {warnings.length}
            </Typography>
            
            <IconButton 
              size="small" 
              onClick={handleNext} 
              disabled={warnings.length <= 1}
              aria-label="next warning"
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Fade>
  );
};
